import { useCallback, useMemo } from 'react'
import { placeholder, filter, includes, conforms } from 'lodash/fp'

import { OfferType, BrandType, GoodType } from '@monorepo/interfaces'
import { useQueryState } from '@monorepo/hooks'
import { useResult } from './useResult'
import { OptionsType } from '@monorepo/deprecated/core/features/SearchEngine'

type ResultType = {
  good: GoodType
  offers: OfferType[]
  meta: {
    isHasMoreOffers: boolean
    isExpanded: boolean
    totalOffers: number
  }
}

type FullResultType<T = ResultType> = {
  good?: ResultType
  crosses: ResultType[]
  originalCrosses: ResultType[]
}

export function useResultControl() {
  const {
    state: { sortBy, excludeBrands: excludeBrandsState },
    updateState,
  } = useQueryState({
    sortBy: undefined,
    excludeBrands: undefined,
  } as Partial<OptionsType>)

  const { brands }: { brands: BrandType[] } = useResult()

  const excludeBrands = useMemo(
    () =>
      filter(
        conforms({ name: includes(placeholder, excludeBrandsState) }),
        brands
      ),
    [brands, excludeBrandsState]
  )

  const changeSortHandler = useCallback(
    (sortBy: string) => {
      updateState('sortBy', sortBy)
    },
    [updateState]
  )

  const changeExcludeBrandsHandler = useCallback(
    (brand: BrandType, disabled: boolean) => {
      const prevState = excludeBrandsState ?? []
      const nextState = disabled
        ? [...prevState, brand.name]
        : prevState.filter((brandName) => brandName !== brand.name)
      updateState('excludeBrands', nextState)
    },
    [excludeBrandsState, updateState]
  )

  const resetFilterHandler = useCallback(() => {
    updateState('excludeBrands', undefined)
  }, [updateState])

  return {
    excludeBrands,
    changeExcludeBrandsHandler,
    sortBy,
    changeSortHandler,
    resetFilterHandler,
  }
}
